// The usage row's feature-layer container (docs/09 §4 Usage row; UI-LAW §8):
// org-wide month-to-date activity counters plus capability-gated hosted billing.

import { useMemo, useState } from "react";

import { useQuery } from "@tanstack/react-query";

import {
  getBillingEntitlement,
  creditUsageState,
  formatCredits,
  humanizePlanKey,
  useCreditBilling,
} from "../lib/billing";
import { useMetric } from "../lib/dashboardData";
import { formatUnits } from "../lib/dashboardMetrics";
import { requestErrorDescription } from "../lib/errors";
import { queryKeys } from "../lib/queryKeys";
import {
  currentMonthKey,
  monthWindowFromKey,
  usageCountersFromResponse,
} from "../lib/usageActivity";
import {
  UsageRow,
  type UsageRowCounter,
  type UsageRowEntitlement,
} from "../ui";

export interface SidebarUsageRowProps {
  onOpenUsageSettings: () => void;
}

function usageRowEntitlement(
  data: Awaited<ReturnType<typeof getBillingEntitlement>>,
): UsageRowEntitlement {
  const creditBalance = Math.max(data.credit_balance, 0);
  const allotment = data.credit_allotment;
  const usageState = creditUsageState(creditBalance, allotment);
  const hasAllotment = allotment != null && allotment > 0;
  const outstandingDebt = data.outstanding_credit_debt;

  return {
    creditBalanceLabel: `${formatCredits(creditBalance)} credits remaining`,
    creditContext: hasAllotment
      ? `${formatCredits(creditBalance)} of ${formatCredits(allotment)} ` +
        "monthly credits remaining."
      : "Monthly credit allotment unavailable.",
    meter: hasAllotment ? { max: allotment, value: creditBalance } : null,
    planName: data.plan_key ? humanizePlanKey(data.plan_key) : "No active plan",
    warning:
      outstandingDebt > 0
        ? {
            message: `${formatCredits(outstandingDebt)} credits outstanding.`,
            role: "alert",
          }
        : usageState === "exhausted"
          ? {
              message:
                "Limit reached. You have used your full monthly allotment.",
              role: "alert",
            }
          : usageState === "warn_80"
            ? {
                message: "80% of your monthly credits used.",
                role: "status",
              }
            : null,
  };
}

export function SidebarUsageRow({ onOpenUsageSettings }: SidebarUsageRowProps) {
  const [isOpen, setIsOpen] = useState(false);
  // The month window is frozen per mount so the query key stays stable; a
  // month rollover mid-session refreshes on the next full load.
  const window = useMemo(() => monthWindowFromKey(currentMonthKey()), []);
  const usage = useMetric({
    bucket: "none",
    group_by: ["event_type"],
    metric: "usage_units",
    window,
  });
  const creditBilling = useCreditBilling();
  const entitlement = useQuery({
    enabled: creditBilling.enabled,
    queryKey: queryKeys.billingEntitlement,
    queryFn: getBillingEntitlement,
  });

  const counters = useMemo<UsageRowCounter[]>(() => {
    if (!usage.data) {
      return [];
    }
    return usageCountersFromResponse(usage.data).map((counter) => ({
      id: counter.id,
      label: counter.label,
      quantity: counter.quantity,
      value: formatUnits(counter.quantity),
    }));
  }, [usage.data]);

  // A failed activity query never becomes a zero. Entitlement is independent:
  // its error never hides loaded activity counters, and vice versa.
  return (
    <UsageRow
      counters={counters}
      entitlement={
        entitlement.data && !entitlement.isError
          ? usageRowEntitlement(entitlement.data)
          : null
      }
      entitlementErrorNote={
        entitlement.isError ? requestErrorDescription(entitlement.error) : null
      }
      errorNote={usage.isError ? requestErrorDescription(usage.error) : null}
      isEntitlementLoading={
        creditBilling.isPending ||
        (creditBilling.enabled && entitlement.isPending)
      }
      isLoading={usage.isPending}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      onOpenUsageSettings={onOpenUsageSettings}
    />
  );
}
